import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Check, X, ChatText, PaperPlaneTilt, Brain, Graph } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { usePlan, type PlanCoveredAction, type PlanKind } from '~/hooks/queries/use-plan';
import { useApprovePlan, useRejectPlan, useDismissPlan } from '~/hooks/mutations/use-plan-decision';

interface PlanCardProps {
  sessionId: string;
  /** Fires after a decision — used by the parent to nudge the agent with a
   *  synthetic turn so it acts right away. For 'approve'/'reject' this is the
   *  existing behaviour; for 'comment' the parent sends the user's notes back
   *  so the agent re-proposes an updated plan.
   *  `background` is true when an approved plan was handed to the durable
   *  background engine; `comment` carries the user's revision notes. */
  onDecision?: (
    decision: 'approve' | 'reject' | 'comment',
    opts?: { background?: boolean; comment?: string; kind?: PlanKind },
  ) => void;
  /** True while the assistant turn is still streaming. FRONT-196: approving a
   *  plan mid-stream opens the consent window early and races the launched
   *  background run against the still-running inline turn, so all decisions are
   *  blocked until the turn finishes. */
  turnActive?: boolean;
}

/**
 * FRONT-196: a plan decision (approve / reject / send-comment) is blocked while
 * any decision request is already in flight, while a rework is pending, OR while
 * the assistant turn is still streaming. The last one is the fix: approving
 * mid-stream opened the consent window early and raced the background run
 * against the still-running inline turn.
 */
export function planActionsBlocked(s: {
  approvePending: boolean;
  rejectPending: boolean;
  reworking: boolean;
  turnActive: boolean;
}): boolean {
  return s.approvePending || s.rejectPending || s.reworking || s.turnActive;
}

interface SavedToGraphCardProps {
  steps: string[];
  onDismiss: () => void;
}

/**
 * FRONT-289: a completed "Save to Graph" plan renders as a persistent "Saved
 * to your knowledge graph" summary instead of vanishing — so the user sees
 * what was saved when they return to the item. The steps shown are exactly
 * the entities/relationships that were written; the link is the path to the
 * real graph (ground truth). FRONT-920: the steps list is capped and
 * internally scrollable so a big graph import can't push the "Open in graph"
 * link past the modal's clip line.
 */
function SavedToGraphCard({ steps, onDismiss }: SavedToGraphCardProps) {
  return (
    <div className="px-4 py-3 mb-2 rounded-xl border border-success/30 bg-success/10">
      <div className="flex items-center gap-2 mb-2">
        <Brain size={15} weight="regular" className="text-success" />
        <span className="text-sm font-medium text-success">
          Saved to your knowledge graph
        </span>
        <Button
          onClick={onDismiss}
          variant="ghost"
          size="icon-xs"
          aria-label="Dismiss"
          className="relative ml-auto -mr-1 text-success/70 hover:bg-success/15 hover:text-success after:absolute after:-inset-2 after:content-['']"
        >
          <X size={14} weight="bold" />
        </Button>
      </div>
      {steps.length > 0 && (
        <div className="max-h-[35vh] overflow-y-auto overscroll-contain pr-1 mb-3">
          <ul className="text-sm text-foreground space-y-1">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-2">
                <Check size={14} weight="bold" className="text-success mt-0.5 shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Link
        to="/memory#graph"
        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        <Graph size={14} weight="regular" /> Open in graph
      </Link>
    </div>
  );
}

interface PlanBodyProps {
  context: string;
  steps: string[];
  verification: string[];
  outOfScope: string[];
}

/**
 * FRONT-3031: the rows approving this plan lets Eddy act on without a card —
 * only those that would otherwise ask, since a row that never asks changes
 * nothing for the person deciding. Exported for its own test.
 */
export function rowsThatSkipTheCard(coveredActions: PlanCoveredAction[] | undefined): string[] {
  return (coveredActions ?? []).filter((c) => c.asks).map((c) => c.name);
}

/**
 * FRONT-920: the plan body (context/Steps/Verification/Out-of-scope) is
 * capped and internally scrollable so a long plan (or a long context blurb)
 * can never push the Approve/Reject actions below the modal's own clip line
 * — the modal panel is overflow-hidden with a fixed max height, so the
 * footer's Reply bar (and this card inside it) never scrolls as a whole.
 * Renders nothing when the plan has no context/steps/verification/out-of-scope.
 */
function PlanBody({ context, steps, verification, outOfScope }: PlanBodyProps) {
  if (!context && steps.length === 0 && verification.length === 0 && outOfScope.length === 0) {
    return null;
  }
  return (
    <div className="max-h-[35vh] overflow-y-auto overscroll-contain pr-1 mb-2">
      {context && (
        <p className="text-sm text-muted-foreground mb-2">{context}</p>
      )}

      {steps.length > 0 && (
        <div className="mb-2">
          <div className="text-[length:var(--text-2xs)] uppercase tracking-wide text-muted-foreground mb-1">Steps</div>
          <ul className="text-sm text-foreground list-disc pl-5 space-y-0.5">
            {steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}

      {verification.length > 0 && (
        <div className="mb-2">
          <div className="text-[length:var(--text-2xs)] uppercase tracking-wide text-muted-foreground mb-1">Verification</div>
          <ul className="text-sm text-foreground list-disc pl-5 space-y-0.5">
            {verification.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}

      {outOfScope.length > 0 && (
        <div className="mb-1">
          <div className="text-[length:var(--text-2xs)] uppercase tracking-wide text-muted-foreground mb-1">Out of scope</div>
          <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-0.5">
            {outOfScope.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * FRONT-3031: what approving the plan lets Eddy do without a card. Kept
 * OUTSIDE the plan body's scroll cap, right above Approve: it is the scope of
 * the consent being given, so it must never be scrolled out of sight.
 */
function PlanCoverage({ coveredActions }: { coveredActions?: PlanCoveredAction[] }) {
  const skipsCard = rowsThatSkipTheCard(coveredActions);
  if (skipsCard.length === 0) return null;
  return (
    <div data-testid="plan-covered-actions" className="mb-2 pt-2 border-t border-border">
      <div className="text-[length:var(--text-2xs)] uppercase tracking-wide text-muted-foreground mb-1.5">
        Will do without asking
      </div>
      <ul className="flex flex-wrap gap-1.5">
        {skipsCard.map((name, i) => (
          <li
            key={`${name}-${i}`}
            className="text-[length:var(--text-xs)] font-medium text-foreground bg-background border border-border rounded-full px-2.5 py-0.5"
          >
            {name}
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-[length:var(--text-xs)] text-muted-foreground text-pretty">Anything else still asks you first.</p>
    </div>
  );
}

interface PlanActionsProps {
  reworking: boolean;
  turnActive: boolean;
  commenting: boolean;
  comment: string;
  setComment: (value: string) => void;
  busy: boolean;
  onSendComment: () => void;
  onCancelComment: () => void;
  onStartComment: () => void;
  onApprove: () => void;
  onReject: () => void;
}

/**
 * The reworking/waiting/commenting/decision states are mutually exclusive —
 * exactly one renders at a time. FRONT-196: Approve/Reject/Add-comments stay
 * disabled (`busy`) while a decision is in flight, a rework is pending, or the
 * assistant turn is still streaming.
 */
function PlanActions({
  reworking,
  turnActive,
  commenting,
  comment,
  setComment,
  busy,
  onSendComment,
  onCancelComment,
  onStartComment,
  onApprove,
  onReject,
}: PlanActionsProps) {
  if (reworking) {
    return (
      <div className="pt-1 text-sm text-muted-foreground">
        Reworking the plan with your comments…
      </div>
    );
  }
  if (turnActive) {
    return (
      <div className="pt-1 text-sm text-muted-foreground">
        Waiting for the agent to finish…
      </div>
    );
  }
  if (commenting) {
    return (
      <div className="pt-1">
        <textarea
          autoFocus
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              onSendComment();
            }
          }}
          rows={3}
          placeholder="What should change? Add your comments and the plan will be reworked…"
          className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <div className="flex gap-2 pt-2">
          <Button
            onClick={onSendComment}
            disabled={!comment.trim() || busy}
            variant="ghost"
            size="sm"
            className="bg-chart-4/20 text-chart-4 hover:brightness-110"
          >
            <PaperPlaneTilt size={14} weight="bold" /> Send to agent
          </Button>
          <Button
            onClick={onCancelComment}
            variant="ghost"
            size="sm"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2 pt-1">
      <Button
        onClick={onApprove}
        disabled={busy}
        variant="ghost"
        size="sm"
        className="bg-success/20 text-success hover:brightness-110"
      >
        <Check size={14} weight="bold" /> Approve
      </Button>
      <Button
        onClick={onReject}
        disabled={busy}
        variant="ghost"
        size="sm"
        className="bg-destructive/20 text-destructive hover:brightness-110"
      >
        <X size={14} weight="bold" /> Reject
      </Button>
      <Button
        onClick={onStartComment}
        disabled={busy}
        variant="ghost"
        size="sm"
        className="text-muted-foreground hover:text-foreground"
      >
        <ChatText size={14} weight="bold" /> Add comments
      </Button>
    </div>
  );
}

export function PlanCard({ sessionId, onDecision, turnActive = false }: PlanCardProps) {
  const { data: plan } = usePlan(sessionId);
  const approve = useApprovePlan();
  const reject = useRejectPlan();
  const dismiss = useDismissPlan();

  // Comment flow: an inline notes box, and a brief "reworking" state after the
  // user sends notes — held until a NEW plan draft arrives (different
  // createdAt), with a safety timeout so the card can never get stuck.
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState('');
  const [reworking, setReworking] = useState(false);
  // FRONT-569: let the user dismiss the persistent "Saved to your knowledge
  // graph" confirmation card. This local flag only gives instant feedback on
  // click — the dismissal is also persisted server-side (dismissedAt), so the
  // card stays hidden when the chat is reopened. The graph write is untouched.
  const [dismissed, setDismissed] = useState(false);
  const sentForCreatedAtRef = useRef<string | null>(null);

  const planCreatedAt = plan?.createdAt;
  useEffect(() => {
    if (!reworking) return;
    // A fresh draft arrived → clear the reworking state and reset the box.
    if (planCreatedAt && planCreatedAt !== sentForCreatedAtRef.current) {
      setReworking(false);
      setCommenting(false);
      setComment('');
      return;
    }
    // Safety net: never leave the card stuck if no new plan shows up.
    const t = setTimeout(() => setReworking(false), 90_000);
    return () => clearTimeout(t);
  }, [reworking, planCreatedAt]);

  // A completed "Save to Graph" plan renders as the persistent confirmation
  // card instead of vanishing — reopen-safe: usePlan refetches on mount and
  // getSessionPlan returns the latest plan (incl. completed ones).
  if (plan && plan.status === 'completed' && plan.kind === 'write_to_graph') {
    // Hide once dismissed — locally for instant feedback, and persistently via
    // the server-stamped dismissedAt so it stays gone after a reopen (FRONT-569).
    if (dismissed || plan.dismissedAt) return null;
    return (
      <SavedToGraphCard
        steps={plan.steps}
        onDismiss={() => {
          setDismissed(true);
          dismiss.mutate({ sessionId });
        }}
      />
    );
  }

  if (!plan || plan.status !== 'pending') return null;

  const busy = planActionsBlocked({
    approvePending: approve.isPending,
    rejectPending: reject.isPending,
    reworking,
    turnActive,
  });

  const handleApprove = () => {
    approve.mutate(
      { sessionId, planId: plan.id },
      {
        onSuccess: (data) =>
          onDecision?.('approve', {
            background: !!data?.background,
            kind: plan.kind,
          }),
      },
    );
  };
  const handleReject = () => {
    reject.mutate(
      { sessionId, planId: plan.id },
      { onSuccess: () => onDecision?.('reject') },
    );
  };
  const handleSendComment = () => {
    const text = comment.trim();
    if (!text) return;
    sentForCreatedAtRef.current = plan.createdAt ?? null;
    setReworking(true);
    onDecision?.('comment', { comment: text });
  };

  return (
    <div className="px-4 py-3 mb-2 rounded-xl border border-border bg-muted/50">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[length:var(--text-2xs)] uppercase tracking-wide bg-chart-4/20 text-chart-4 px-1.5 py-0.5 rounded font-medium">
          Plan
        </span>
        <span className="text-sm font-medium text-foreground truncate">{plan.title}</span>
      </div>

      <PlanBody
        context={plan.context}
        steps={plan.steps}
        verification={plan.verification}
        outOfScope={plan.outOfScope}
      />

      <PlanCoverage coveredActions={plan.coveredActions} />

      <PlanActions
        reworking={reworking}
        turnActive={turnActive}
        commenting={commenting}
        comment={comment}
        setComment={setComment}
        busy={busy}
        onSendComment={handleSendComment}
        onCancelComment={() => {
          setCommenting(false);
          setComment('');
        }}
        onStartComment={() => setCommenting(true)}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
}
